import React, { useState } from 'react';

import { MenuItem } from '@mui/material';
import Divider from '@mui/material/Divider';
import { WHITELISTED_AI_NARRATIVE_COMPONENT_KEYS } from 'constants/componentKeys/whiteListedAiNarrative.componentKeys';
import { IconMenuItem } from 'mui-nested-menu';
import { useHistory } from 'react-router-dom';
import { emitHover } from 'services/eventBus/hoverBus';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import useUserHook from 'hooks/useUserHook';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { ShareInsightModal } from 'components/common/ShareInsightModal/ShareInsightModal';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { IComponentProps, IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { WidgetButton } from 'components/Dashboards/Reporting/_common/widgetButtons/WidgetButton';
import classes from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu.module.scss';
import { WidgetSizesMenu } from 'components/Dashboards/Reporting/widgetInReport/components/WidgetHeader/SizesMenu';
import { settingsConfig } from 'components/Settings/settingsConfig';
import { CustomizedDropdownMenu } from 'components/common';

interface Props {
    widgetViewMode?: IComponentProps['widgetViewMode'];
    widgetId?: IComponentProps['widgetId'];
    componentKey: string;
    parentComponentKey?: string;
    shareTitle?: string | undefined;
    shareLink?: string | undefined;
    afterDelete?: () => Promise<void> | void;
    projectId?: IComponentProps['projectId'];
    contractId?: IComponentProps['contractId'];
}

const MENU_FONT_SIZE = '13px';

export const ThreeDotsMenu = ({
    widgetViewMode = WidgetView.page,
    widgetId = undefined,
    componentKey,
    shareTitle,
    shareLink,
    afterDelete,
    projectId,
    contractId,
    parentComponentKey,
}: Props) => {
    const history = useHistory();
    const { ability } = useUserHook();

    const { onDeleteWidget, onWidgetChangeSize } = usePinboardWidgetWrapperContext();

    const isAdmin = ability.can('all', 'Admin');

    const [isShareModalOpen, setIsShareModalOpen] = useState(false);

    const OnDelete = async () => {
        onDeleteWidget?.();
        if (afterDelete) {
            await afterDelete();
        }
    };

    const onOpenShareModal = () => {
        setIsShareModalOpen(true);
    };

    const onRemoveBlueHighlightInNarratives = () => {
        // Clears highlight state for all narrative-related widgets (used when 3-dots menu opens)
        emitHover(null);
    };

    const onRouteToNarrativesTemplateEditor = () => {
        const query = {
            componentKey: parentComponentKey ?? componentKey ?? 'component-not-found',
        };

        const link = AppRoutes.buildPathWithQuery([AppRoutes.SETTINGS, settingsConfig.narrativeTemplates.link], query);
        history.push(link);
    };

    const shouldRenderEditNarrativeTemplates =
        WHITELISTED_AI_NARRATIVE_COMPONENT_KEYS.includes(componentKey as keyof IHashmap) && isAdmin;

    return (
        <div className={classes.threeDotsMenu} onClick={onRemoveBlueHighlightInNarratives}>
            <CustomizedDropdownMenu
                key={'menu'}
                triggerElement={<WidgetButton icon={'more_vert'} tooltipContent={'More...'} />}
            >
                {onWidgetChangeSize && WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <MenuItem>
                        <WidgetSizesMenu
                            widgetId={widgetId}
                            changeWidgetSize={onWidgetChangeSize}
                            widgetComponentKey={componentKey}
                        />
                    </MenuItem>
                )}

                {shareTitle && shareLink && (
                    <MenuItem>
                        <IconMenuItem
                            sx={{
                                '& .MuiTypography-root': {
                                    fontSize: MENU_FONT_SIZE,
                                    position: 'relative',
                                },
                                '& .MuiBox-root': {
                                    alignItems: 'center',
                                },
                            }}
                            leftIcon={<MuiIcon icon={'share_outlined'} fontSize={'16px'} />}
                            key={'share'}
                            onClick={onOpenShareModal}
                            label={'Share'}
                        />
                    </MenuItem>
                )}
                {shouldRenderEditNarrativeTemplates && (
                    <MenuItem>
                        <IconMenuItem
                            sx={{
                                '& .MuiTypography-root': {
                                    fontSize: MENU_FONT_SIZE,
                                    position: 'relative',
                                },
                                '& .MuiBox-root': {
                                    alignItems: 'center',
                                },
                            }}
                            leftIcon={<MuiIcon icon={'rtt'} fontSize={'16px'} />}
                            key={'editNarrativeTemplate'}
                            onClick={onRouteToNarrativesTemplateEditor}
                            label={'Edit narrative template'}
                        />
                    </MenuItem>
                )}
                {onDeleteWidget && WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <>
                        <Divider />
                        <MenuItem>
                            <IconMenuItem
                                sx={{
                                    '& .MuiTypography-root': {
                                        fontSize: MENU_FONT_SIZE,
                                        position: 'relative',
                                        color: `var(--menu-destructive-fg)`,
                                    },
                                    '& .MuiBox-root': {
                                        alignItems: 'center',
                                    },
                                }}
                                leftIcon={
                                    <MuiIcon
                                        icon={'delete'}
                                        type={'outlined'}
                                        fontSize={'16px'}
                                        color={'var(--menu-destructive-fg)'}
                                    />
                                }
                                onClick={OnDelete}
                                label={'Remove component'}
                            />
                        </MenuItem>
                    </>
                )}
            </CustomizedDropdownMenu>
            <ShareInsightModal
                title={shareTitle ?? ''}
                link={shareLink ?? ''}
                isModalOpen={isShareModalOpen}
                setIsModalOpen={setIsShareModalOpen}
            />
        </div>
    );
};
